/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions */


import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { GlobalAuthContext } from 'store/context';
import { useTextMessage } from '@fitinsur/hooks';
import { LoaderContext } from '@fitinsur/contexts';
import { useGetAccessGroups } from '@fitinsur/services/AccessGroupService';
import { generalHandler } from './sectionHandlers';
import { useGetOperationAsync } from '@fitinsur/services/OperationService';
import { useGetQuestionnairesByIdProduct } from '@fitinsur/services/QuestionnairesService';
import { useCheckUsername, useUpdateAdminUserOperation } from '@fitinsur/services/AdminService';
import { FitinsurAPI, MessageCenter } from '@fitinsur/utils';
import { CheckUsernameButton } from './styles';
import { useGetUserByPerson } from '@fitinsur/services/UserService';
import {
  FormSectionGlobal as FormSection,
  FormContainer,
  Icon,
  Label,
  ButtonContainer,
  Button,
  HasPermission,
  ResponsiveTable
} from '@fitinsur/components';
import Layout from 'components/Layout';
import { components } from './sectionHandlers/person/components';
import { useGetAccessGroupMembership } from '@fitinsur/services/AccessGroupMembershipService';

export default function ManagementEditUser({ permissions, match, ...props }) {
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const history = useHistory();
  const formikRef = useRef();
  const mc = new MessageCenter();
  const fitinsurAPI = new FitinsurAPI();
  const authContext = useContext(GlobalAuthContext);
  const getQuestionnaire = useRef(false);
  const { getDescription } = useTextMessage();
  const [questionnaire, setQuestionnaire] = useState([]);
  const sentUpdateAdminOperation = useRef(false);
  const [personData, setPersonData] = useState(null);
  const [operation, setOperation] = useState(null);
  const [forceLoading, setForceLoading] = useState(false);
  const [isUsernameAvailable, setIsUsernameAvailable] = useState(false);
  const [isBound, setIsBound] = useState(false);
  const [accessGroups, setAccessGroups] = useState([]);
  const [renderAccessGroupsTable, setRenderAccessGroupsTable] = useState(false);
  const [users, setUsers] = useState([]);

  const SECTIONS = {
    USER_ACCESS_GROUP: { name: 'USER_ACCESS_GROUP', isMultiple: false, isRisk: false },
    PERSON: { name: 'PERSON', isMultiple: false, isRisk: false },
    PERSON_USER: { name: 'PERSON_USER', isMultiple: false, isRisk: false }
  };

  const accessGroupColumnsProps = {
    history,
    getDescription, 
    setAccessGroups,
    setRenderAccessGroupsTable
  };

  const {
    data: useGetQuestionnairesByIdProductData,
    loading: useGetQuestionnairesByIdProductLoading,
    status: useGetQuestionnairesByIdProductStatus,
    request: useGetQuestionnairesByIdProductRequest,
  } = useGetQuestionnairesByIdProduct(match.params.idProduct);

  const {
    loading: useGetOperationLoading,
    request: useGetOperationRequest,
  } = useGetOperationAsync(match.params.idOperation);

  const {
    loading: useCheckUsernameLoading,
    request: useCheckUsernameRequest,
  } = useCheckUsername();

  const {
    loading: useGetUserByPersonLoading,
    request: useGetUserByPersonRequest,
  } = useGetUserByPerson(match.params.idPerson);

    const {
      loading: useGetAccessGroupsLoading,
      request: useGetAccessGroupsRequest,
    } = useGetAccessGroups();

  const {
    loading: useGetAccessGroupMembershipLoading,
    request: useGetAccessGroupMembershipRequest,
  } = useGetAccessGroupMembership();

  const {
    loading: useUpdateAdminOperationLoading,
    request: useUpdateAdminOperationRequest,
  } = useUpdateAdminUserOperation();

  async function getPersonData() {
    if (!match.params.idPerson) return;

    try {
      setForceLoading(true);
      fitinsurAPI.setAuthTokenByParam(authContext.token);
      const response = await fitinsurAPI.requestGetPersonsInfoWithTailRelationship(match?.params?.idPerson);
      const { ret_value } = await response.json();
      setPersonData(ret_value);
    } catch (err) {
      mc.error(err);
    } finally {
      setForceLoading(false);
    }
  }

  async function getQuestionnaireData() {
    if (getQuestionnaire.current) return;
    getQuestionnaire.current = true;

    try {
      if (match?.params?.idOperation) {
        const respOperation = await useGetOperationRequest(null, { loadLastDocument: true });
        if (respOperation.status === 200) {
          const { data: { value } } = respOperation;
          setOperation(value);
          setIsBound([].includes(value.idDomainStatus));
        }
      }

      await useGetQuestionnairesByIdProductRequest(null, { idDomain: 752, idDomainStatus: 452 });
    } catch (err) {
      mc.error(err);
    }
  }

  async function handleGetUserByNameRequest(userName) {
    if (!userName) return;

    try {
      await useCheckUsernameRequest({}, { userName });

      setIsUsernameAvailable(true);
    } catch (err) {
      setIsUsernameAvailable(false);

      if (err?.response?.data?.errors?.length) {
        err?.response?.data?.errors.map(error => mc.error(error.message))
      } else {
        mc.error(err);
      }
    }
  }

  async function getAccessGroups(idUser) {
    if (!idUser) return;

    const { data: { value: groupsMembership } } = await useGetAccessGroupMembershipRequest(null, { idUser });
    const idGroups = groupsMembership?.map(group => group.idGroup);
    const { data: { value } } = await useGetAccessGroupsRequest(idGroups);
    const accessGroups = value?.map(group => ({
      idGroup: group.idGroup,
      groupName: group.groupName,
      behavior: group.behavior,
    }));

    if (accessGroups) {
      setAccessGroups(accessGroups);
      setRenderAccessGroupsTable(true);
    } else {
      setRenderAccessGroupsTable(false);
    }
  }

  async function handleAddAccessGroupToUser(setFieldValue, questionnaireUpdated) {
    try {
      const qstAccesGroup = questionnaireUpdated.find(question => question.idQuestion === 1905);

      if (!qstAccesGroup.answerJson) return;

      const { id: idGroup } = JSON.parse(qstAccesGroup.answerJson);

      setFieldValue(qstAccesGroup.key, "");

      if (!idGroup || accessGroups.find(group => group.idGroup === idGroup)) return;

      const { data: { value } } = await useGetAccessGroupsRequest([idGroup]);

      const accessGroup = value?.map(group => ({
        idGroup: group.idGroup,
        groupName: group.groupName,
        behavior: group.behavior,
      }));

      !renderAccessGroupsTable && setRenderAccessGroupsTable(true);

      setAccessGroups([...accessGroups, ...accessGroup]);
    } catch (err) {
      mc.error(err);
    }
  }

  async function getUsers() {
    try {
      const { data: { value } } = await useGetUserByPersonRequest();

      setUsers(value);
      if(value?.idUser) await getAccessGroups(value.idUser);
    } catch (err) {
      mc.error(err);
    }
  }

  useEffect(() => {
    getQuestionnaireData();
    getPersonData();
    getUsers();
  }, []);

  useEffect(() => {
    setQuestionnaire(prev => {
      return prev.map(question => {
        if (question.idQuestion === 1905) {
          question.idAccessGroups = accessGroups.map(group => group.idGroup);
        }
        return question;
      })
    })
  }, [accessGroups]);

  useEffect(() => {
    if (
      useGetQuestionnairesByIdProductStatus === 200 &&
      ((match.params.idPerson && personData) || !match.params.idPerson) 
    ) {
      generalHandler.events.handleQuestionnaire({
        data: useGetQuestionnairesByIdProductData?.value,
        setQuestionnaire,
        setFieldValue: formikRef?.current?.setFieldValue,
        personData,
        users,
        accessGroups
      });
    }
  }, [
    useGetQuestionnairesByIdProductStatus,
    useGetQuestionnairesByIdProductData,
    personData,
  ]);
  
  useEffect(() => {
    updateIsLoadingStatus(
      forceLoading ||
      useGetQuestionnairesByIdProductLoading ||
      useGetOperationLoading ||
      useCheckUsernameLoading ||
      useGetAccessGroupMembershipLoading ||
      useUpdateAdminOperationLoading ||
      useGetUserByPersonLoading ||
      useGetAccessGroupsLoading
    );
  }, [
    useGetQuestionnairesByIdProductLoading,
    useGetOperationLoading,
    forceLoading,
    useCheckUsernameLoading,
    useGetAccessGroupMembershipLoading,
    useUpdateAdminOperationLoading,
    useGetUserByPersonLoading,
    useGetAccessGroupsLoading
  ]);

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{}}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ values, setFieldValue, errors, setFieldError }) => (
          <Layout idOperation={props?.idOperation} title={"Dados do Usuário"}>
            <Form>
              {/* Informações cadastrais da parte */}
              <FormContainer
                noTitlePadding
                title={getDescription("DOMAINS", 755)}
                hide={
                  !generalHandler.events.sectionIsVisible({
                    sectionName: "PERSON",
                    questionnaire,
                    permissions,
                    currentModality: null,
                    ignoreVisibilityByModality: true,
                  })
                }
              >
                <FormSection
                  setQuestionnaire={setQuestionnaire}
                  sectionName="PERSON"
                  disableAll={true}
                  idProduct={match.params.idProduct}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  questionnaire={questionnaire}
                  ignoreModality
                  permissions={permissions}
                />
              </FormContainer>

              {/* Informações de usuário */}
              <FormContainer
                noTitlePadding
                title={getDescription("DOMAINS", 806)}
                hide={
                  !generalHandler.events.sectionIsVisible({
                    sectionName: "PERSON_USER",
                    questionnaire,
                    permissions,
                    currentModality: null,
                    ignoreVisibilityByModality: true,
                  })
                }
              >
                <FormSection
                  setQuestionnaire={setQuestionnaire}
                  sectionName="PERSON_USER"
                  disableAll={false}
                  idProduct={match.params.idProduct}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  setFieldError={setFieldError}
                  questionnaire={questionnaire}
                  ignoreModality
                  permissions={permissions}
                  extraButtonsQuestion={(question) => {
                    return (
                      <>
                        {question.idQuestion === 2425 && (
                          <div>
                            <CheckUsernameButton
                              type="button"
                              onClick={() =>
                                handleGetUserByNameRequest(values[question.key])
                              }
                            >
                              <Icon
                                name={
                                  isUsernameAvailable
                                    ? "check-ok-true-green"
                                    : "check-ok"
                                }
                                title="Consultar disponibilidade de username"
                              />
                            </CheckUsernameButton>
                          </div>
                        )}
                      </>
                    );
                  }}
                  onQuestionChange={() =>
                     setIsUsernameAvailable(false)
                  }
                />
              </FormContainer>

              {/* Grupo de acesso */}
              {questionnaire?.length > 0 && match.params.idUser && (
                <FormContainer
                  noTitlePadding
                  title={getDescription("DOMAINS", 853)}
                >
                  <FormSection
                    setQuestionnaire={setQuestionnaire}
                    sectionName="USER_ACCESS_GROUP"
                    disableAll={isBound}
                    idProduct={match.params.idProduct}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    questionnaire={questionnaire}
                    ignoreModality
                    permissions={permissions}
                    searchRequest={match.params.idPerson}
                    onQuestionChange={(question, value, setFieldValue, questionnaireUpdated) =>
                      handleAddAccessGroupToUser(setFieldValue, questionnaireUpdated)
                    }
                  />
                  {renderAccessGroupsTable ? (
                      <ResponsiveTable
                        columns={components.accessGroupColumns(
                          accessGroupColumnsProps
                        )}
                        data={accessGroups}
                        formikRef={formikRef?.current}
                      />
                    ) : (
                      <Label fontSize={15} style={{ marginBottom: 15 }}>
                        {getDescription("DOMAINS", 851)}
                      </Label>
                    )}
                </FormContainer>
              )}
            </Form>
            <ButtonContainer>
              {/* Voltar */}
              <Button forth onClick={() => history.goBack()}>
                &lt; {getDescription("DOMAINS", 758)}
              </Button>

              {/* Salvar */}
              <HasPermission
                permission={permissions.CREATE_USER || permissions.EDIT_USER}
              >
                <Button
                  id="btnSave"
                  type="button"
                  onClick={() => {
                    generalHandler.events.handleSave({
                      values,
                      questionnaire,
                      idPerson: match.params.idPerson,
                      idOperation: match.params.idOperation,
                      idOperationParent: operation?.idOperationParent,
                      SECTIONS,
                      isDraft: false,
                      getDescription,
                      sentUpdateAdminOperation,
                      useUpdateAdminOperationRequest,
                      mc,
                      formikRef,
                      history,
                      userParentInfo: {
                        userPersonParent: match.params.idPerson,
                      },
                      save: true,
                      setFieldError,
                      isUsernameAvailable,
                      users,
                      idUser: match.params.idUser,
                      accessGroupIds: accessGroups.map(group => group.idGroup),
                    });
                  }}
                  primary
                >
                  {getDescription("DOMAINS", 759)}
                </Button>
              </HasPermission>
            </ButtonContainer>
          </Layout>
        )}
      </Formik>
    </>
  );
}